import { Sparkles } from 'lucide-react'
import { useCallback, useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Button } from '@/components/ui/button'
import { useAuth } from '@/contexts/AuthContext'
import { claimUnseenWhatsNewFeatures, markWhatsNewFeatureSeen } from './intro-visit'
import { type FeatureSlide, featureSlides } from './slides'
import { WhatsNewDialog } from './WhatsNewDialog'

const featureSlideIds = featureSlides.map(slide => slide.id)

function UserFeatureTour({ userId, autoOpen }: { userId: string; autoOpen: boolean }) {
  const { t } = useTranslation()
  const [open, setOpen] = useState(false)
  const [activeSlides, setActiveSlides] = useState<readonly FeatureSlide[]>(featureSlides)
  const handleSlideViewed = useCallback(
    (featureId: string) => markWhatsNewFeatureSeen(userId, featureId),
    [userId]
  )

  useEffect(() => {
    if (!autoOpen) return
    const unseenIds = new Set(claimUnseenWhatsNewFeatures(userId, featureSlideIds))
    if (unseenIds.size === 0) return
    setActiveSlides(featureSlides.filter(slide => unseenIds.has(slide.id)))
    setOpen(true)
  }, [autoOpen, userId])

  return (
    <WhatsNewDialog
      open={open}
      onOpenChange={next => {
        if (next) setActiveSlides(featureSlides)
        setOpen(next)
      }}
      tour={{ slides: activeSlides, onSlideViewed: handleSlideViewed }}
      trigger={
        <Button
          variant="ghost"
          size="sm"
          className="shrink-0 text-muted-foreground max-sm:size-9 max-sm:px-0"
          aria-label={t('whatsNew.title')}
        >
          <Sparkles className="size-4" aria-hidden />
          <span className="hidden sm:inline">{t('whatsNew.title')}</span>
        </Button>
      }
    />
  )
}

export function WhatsNewLauncher({ autoOpen }: { autoOpen: boolean }) {
  const { user } = useAuth()
  return user ? <UserFeatureTour key={user.id} userId={user.id} autoOpen={autoOpen} /> : null
}
